import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Dialog } from 'primereact/dialog';
import { getCourseBatch, startCourseRuns } from '../../api/reviews';
import './CourseReviewModal.css';

const POLL_INTERVAL = 2000;


// What the dialog says while and after each kind of batch runs.
const KIND_TEXT = {
    review: { running: 'Reviewing cards…', done: 'Review complete', count: 'reviewed', open: 'findings' },
    proofread: { running: 'Proofreading cards…', done: 'Proofread complete', count: 'proofread', open: 'typos' },
};

const STATUS_PILL = {
    running: { className: 'crm-pill crm-pill-running', label: 'Running' },
    done: { className: 'crm-pill crm-pill-done', label: 'Done' },
    error: { className: 'crm-pill crm-pill-error', label: 'Error' },
};

// Each card of the batch with its status and, once checked, its open
// findings. Inside the editor a card opens in place (onOpenCard); elsewhere
// it opens the editor in a new tab.
function CardRows({ cards, courseId, kind, onOpenCard }) {
    return (
        <div className="crm-card-list">
            {cards.map((c) => {
                const pill = STATUS_PILL[c.status] || STATUS_PILL.running;
                const title = c.card_title || `Card ${c.card_id}`;
                return (
                    <div key={c.card_id} className="crm-card-row">
                        {onOpenCard ? (
                            <button type="button" className="crm-card-link crm-card-button" onClick={() => onOpenCard(c.card_id)}>
                                {title}
                            </button>
                        ) : (
                            <a
                                href={`/editor?course=${courseId}&card=${c.card_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="crm-card-link"
                            >
                                {title}
                            </a>
                        )}
                        {c.open > 0 && <span className="crm-open">{c.open} {KIND_TEXT[kind].open}</span>}
                        <span className={pill.className}>{pill.label}</span>
                    </div>
                );
            })}
        </div>
    );
}

export default function CourseReviewModal({ visible, course, token, beforeRun, onClose, onOpenCard }) {
    const [phase, setPhase] = useState('idle'); // idle | running | done | error
    const [batchId, setBatchId] = useState(null);
    const [kind, setKind] = useState('review'); // what the batch runs: review | proofread
    const [progress, setProgress] = useState(null); // { total, completed, running, errored, cards: [...] }
    const [errorMsg, setErrorMsg] = useState('');
    const [skipUnchanged, setSkipUnchanged] = useState(true);
    const [skipped, setSkipped] = useState(0); // cards a proofread left out as unchanged

    const pollTimerRef = useRef(null);
    const mountedRef = useRef(true);

    const stopPolling = useCallback(() => {
        if (pollTimerRef.current) {
            clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
        }
    }, []);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            stopPolling();
        };
    }, [stopPolling]);

    // Reset every time the modal opens for a new course
    useEffect(() => {
        if (visible) {
            setPhase('idle');
            setKind('review');
            setBatchId(null);
            setProgress(null);
            setErrorMsg('');
            setSkipped(0);
        } else {
            stopPolling();
        }
    }, [visible, course?.id, stopPolling]);

    const pollBatch = useCallback(async (id) => {
        try {
            const data = await getCourseBatch(token, course.id, id);
            if (!mountedRef.current) return;
            setProgress(data);
            if (data.running === 0 && data.total > 0) {
                stopPolling();
                setPhase('done');
            } else if (data.total === 0) {
                stopPolling();
                setPhase('done');
            }
        } catch {
            // silently retry on next interval
        }
    }, [course?.id, token, stopPolling]);

    const startReview = useCallback(async (batchKind) => {
        if (!course?.id) return;
        setErrorMsg('');
        setKind(batchKind);
        setPhase('running');
        setProgress(null);
        // The checks read saved bodies, so the open card's edits are saved first.
        if (beforeRun && !(await beforeRun())) {
            if (!mountedRef.current) return;
            setErrorMsg('The open card could not be saved, so the check would miss your latest edits. Save it, then try again.');
            setPhase('error');
            return;
        }
        try {
            const data = await startCourseRuns(token, course.id, batchKind, {
                skipUnchanged: batchKind === 'proofread' && skipUnchanged,
            });
            if (!mountedRef.current) return;
            setBatchId(data.batch_id);
            setSkipped(data.skipped_cards || 0);

            if (data.total_cards === 0) {
                setProgress({
                    batch_id: data.batch_id,
                    total: 0,
                    completed: 0,
                    running: 0,
                    errored: 0,
                    cards: [],
                });
                setPhase('done');
                return;
            }

            // Kick off polling immediately, then on interval
            pollBatch(data.batch_id);
            pollTimerRef.current = setInterval(() => pollBatch(data.batch_id), POLL_INTERVAL);
        } catch (err) {
            if (!mountedRef.current) return;
            setErrorMsg(err.message || 'Failed to start review.');
            setPhase('error');
        }
    }, [course?.id, token, beforeRun, pollBatch, skipUnchanged]);

    const handleClose = useCallback(() => {
        stopPolling();
        onClose({
            courseId: course?.id,
            ranBatch: !!batchId,
        });
    }, [stopPolling, onClose, course?.id, batchId]);

    const renderIdle = () => (
        <div className="crm-center">
            <div className="crm-icon"><i className="pi pi-check-square" /></div>
            <h3 className="crm-title">Check every card of the course</h3>
            <p className="crm-desc">
                A proofread checks spelling, grammar and punctuation (about 20 s a card).
                A full review also checks facts, outdated info and style (about 2 min a card).
            </p>
            <label className="crm-skip">
                <input type="checkbox" checked={skipUnchanged} onChange={e => setSkipUnchanged(e.target.checked)} />
                Proofread: skip cards unchanged since their last proofread
            </label>
            <div className="crm-actions crm-actions-center">
                <button className="crm-btn crm-btn-primary" onClick={() => startReview('proofread')}>
                    <i className="pi pi-check-square" /> Proofread
                </button>
                <button className="crm-btn" onClick={() => startReview('review')}>
                    <i className="pi pi-shield" /> Full review
                </button>
            </div>
        </div>
    );

    const skippedNote = skipped > 0 ? ` · ${skipped} unchanged, skipped` : '';

    const renderRunning = () => {
        const total = progress?.total || 0;
        const completed = (progress?.completed || 0) + (progress?.errored || 0);
        const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
        return (
            <div className="crm-running">
                <div className="crm-progress-row">
                    <div className="crm-spinner" />
                    <div className="crm-progress-text">
                        <div className="crm-progress-title">{KIND_TEXT[kind].running}</div>
                        <div className="crm-progress-sub">
                            {completed} / {total || '?'} cards complete{skippedNote}
                        </div>
                    </div>
                </div>
                <div className="crm-progress-track">
                    <div className="crm-progress-fill" style={{ width: `${percent}%` }} />
                </div>
                <CardRows cards={progress?.cards || []} courseId={course?.id} kind={kind} onOpenCard={onOpenCard} />
            </div>
        );
    };

    const doneSummary = (total, completed, errored) => {
        if (total === 0) {
            return skipped > 0
                ? `All ${skipped} cards are unchanged since their last proofread.`
                : 'No cards in this course.';
        }
        return `${completed} ${KIND_TEXT[kind].count}${errored ? `, ${errored} errored` : ''}${skippedNote}`;
    };

    const renderDone = () => {
        const total = progress?.total || 0;
        const completed = progress?.completed || 0;
        const errored = progress?.errored || 0;
        return (
            <div className="crm-running">
                <div className="crm-done-summary">
                    <i className="pi pi-check-circle crm-done-icon" />
                    <div>
                        <div className="crm-done-title">{KIND_TEXT[kind].done}</div>
                        <div className="crm-done-sub">{doneSummary(total, completed, errored)}</div>
                    </div>
                </div>
                {(progress?.cards || []).length > 0 && (
                    <CardRows cards={progress.cards} courseId={course?.id} kind={kind} onOpenCard={onOpenCard} />
                )}
                <div className="crm-actions">
                    <button className="crm-btn crm-btn-primary" onClick={handleClose}>
                        Close
                    </button>
                </div>
            </div>
        );
    };

    const renderError = () => (
        <div className="crm-center">
            <div className="crm-icon crm-icon-error"><i className="pi pi-exclamation-circle" /></div>
            <h3 className="crm-title">The check could not start</h3>
            <p className="crm-desc">{errorMsg}</p>
            <div className="crm-actions">
                <button className="crm-btn" onClick={handleClose}>Close</button>
                <button className="crm-btn crm-btn-primary" onClick={() => setPhase('idle')}>
                    <i className="pi pi-refresh" /> Try again
                </button>
            </div>
        </div>
    );

    return (
        <Dialog
            visible={visible}
            header={course?.name ? `Check course: ${course.name}` : 'Check course'}
            modal
            style={{ width: '560px', maxWidth: '95vw' }}
            onHide={handleClose}
            className="course-review-dialog"
        >
            {phase === 'idle' && renderIdle()}
            {phase === 'running' && renderRunning()}
            {phase === 'done' && renderDone()}
            {phase === 'error' && renderError()}
        </Dialog>
    );
}

CourseReviewModal.propTypes = {
    visible: PropTypes.bool.isRequired,
    course: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
    }),
    token: PropTypes.string.isRequired,
    // Saves pending edits before a batch starts; false when the save failed.
    beforeRun: PropTypes.func,
    onClose: PropTypes.func.isRequired,
    // Open a card in place (the editor); without it cards open the editor in a new tab.
    onOpenCard: PropTypes.func,
};

CourseReviewModal.defaultProps = {
    course: null,
    beforeRun: null,
    onOpenCard: null,
};
